import { getProductUrlSlugSegment } from './productUrl';
import { isDevSeedProduct } from './devSeedProductNames';

function normalizeSpecifications(product) {
  return (Array.isArray(product?.specifications) ? product.specifications : [])
    .map((row) => ({
      key: String(row?.key || '').trim(),
      value: String(row?.value || '').trim()
    }))
    .filter((row) => row.key && row.value);
}

/** Flatten Mongo / Railway / string subcategory fields from a product row (catalog or Mongo). */
export function parseSubcategoryFromProduct(product = {}) {
  const variants = [
    product.subCategory,
    product.subcategory,
    product.productSubcategory,
    product.productSubCategory
  ];
  const obj = variants.find((v) => v && typeof v === 'object' && !Array.isArray(v)) || null;

  const takeString = (...vals) => {
    for (const v of vals) {
      if (v == null) continue;
      const s = String(v).trim();
      if (s && s !== '[object Object]') return s;
    }
    return '';
  };

  const mongoId = takeString(
    obj?._id,
    obj?.id,
    product.subCategory,
    product.subcategory,
    product.subCategoryMongoId,
    product.subcategoryMongoId
  );
  const railwayId = takeString(
    obj?.railwayId,
    obj?.categorySubId,
    product.subCategoryId,
    product.subcategoryId,
    product.productSubCategoryId,
    product.productSubcategoryId
  );
  const name = takeString(
    obj?.name,
    product.subCategoryName,
    product.subcategoryName,
    product.productSubCategoryName,
    product.productSubcategoryName,
    typeof product.subCategory === 'string' ? product.subCategory : '',
    typeof product.subcategory === 'string' ? product.subcategory : ''
  );

  return {
    subCategory: mongoId || null,
    subCategoryId: railwayId || null,
    subCategoryName: name || null
  };
}

/**
 * True when the product can be purchased.
 * Railway marks stock only via `isOutOfStock` (true = out, false = in) — not inventory.quantity.
 */
export function isProductInStock(product = {}) {
  if (product.isOutOfStock === true) return false;
  if (product.isOutOfStock === false) return true;
  return product.isAvailable !== false;
}

/**
 * Products shown in grids/search — includes out-of-stock; hides admin-disabled/deleted only.
 */
export function filterBrowsableProducts(productList = []) {
  return (productList || []).filter((p) => {
    if (p == null) return false;
    if (isDevSeedProduct(p)) return false;
    if (p.isDeleted === true || p.deletedByAdmin === true) return false;
    if (p.isActive === false) return false;
    return true;
  });
}

/** API / cache rows before storefront shaping. */
export function isRawCatalogProductActive(row = {}) {
  if (!row || isDevSeedProduct(row)) return false;
  if (row.isDeleted === true || row.deletedByAdmin === true) return false;
  if (row.isActive === false) return false;
  return true;
}

export function filterRawCatalogProducts(rows = []) {
  return (rows || []).filter(isRawCatalogProductActive);
}

/** Set of ids for products currently in the live storefront catalog. */
export function buildBrowsableProductIdSet(productList = []) {
  const set = new Set();
  filterBrowsableProducts(productList).forEach((p) => {
    if (p?.id != null) set.add(String(p.id));
    if (p?._id != null) set.add(String(p._id));
  });
  return set;
}

/** False when product detail would 404 (deleted/hidden/not in catalog). */
export function isProductOpenableInCatalog(product, browsableIdSet) {
  if (!product) return false;
  if (!filterBrowsableProducts([product]).length) return false;
  if (!browsableIdSet || browsableIdSet.size === 0) {
    return filterBrowsableProducts([product]).length > 0;
  }
  const pool =
    product.offerGroup?.offers?.length > 0 ? product.offerGroup.offers : [product];
  return pool.some((item) => {
    const id = item?.id ?? item?._id;
    return id != null && browsableIdSet.has(String(id));
  });
}

/**
 * One shared shape for catalog API rows and Mongo rows so ProductGrid / ProductCard match DataContext.
 * Use for server-filter fetches that bypass the provider's initial `transformProduct` map.
 */
export function shapeProductForStorefront(product) {
  const priceNum = parseFloat(product.price) || 0;
  const discountPriceNum = product.discountPrice != null ? parseFloat(product.discountPrice) : null;
  const isOnDiscount = product.isOnDiscount && discountPriceNum != null;
  const displayPrice = isOnDiscount ? discountPriceNum : priceNum;
  const originalNum = product.originalPrice != null ? parseFloat(product.originalPrice) : priceNum;
  let discountPct = product.discount != null ? product.discount : null;
  if (discountPct == null && originalNum > 0 && displayPrice < originalNum) {
    discountPct = Math.min(100, Math.max(0, Math.round(((originalNum - displayPrice) / originalNum) * 100)));
  }
  if (discountPct == null && priceNum > 0 && isOnDiscount) {
    discountPct = Math.round((1 - discountPriceNum / priceNum) * 100);
  }
  if (discountPct == null) discountPct = 0;
  let imageUrls = [];
  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    const first = product.images[0];
    if (typeof first === 'string') {
      imageUrls = product.images.filter(Boolean);
    } else {
      const withDefault = [...product.images].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
      imageUrls = withDefault.map((img) => img.imageUrl || img.url || (typeof img === 'string' ? img : null)).filter(Boolean);
    }
  }
  if (imageUrls.length === 0 && product.image) imageUrls = [product.image];
  if (imageUrls.length === 0) imageUrls = ['/images/products/electronics.jpg'];
  const idVal = product._id || product.id;
  const shaped = {
    id: idVal,
    _id: idVal,
    name: product.name,
    description: product.description || '',
    brand: product.brand || '',
    model: product.model || '',
    sku: product.sku != null ? String(product.sku).trim() : '',
    price: displayPrice,
    originalPrice: originalNum,
    discount: discountPct,
    category:
      product.category != null
        ? String(product.category.id || product.category._id || product.category)
        : product.categoryId != null
          ? String(product.categoryId)
          : '',
    categoryName: product.category?.name || product.categoryName,
    categorySlug:
      product.category?.slug ||
      product.categorySlug ||
      (product.category?.name || '').toLowerCase().replace(/\s+/g, '-'),
    ...parseSubcategoryFromProduct(product),
    images: imageUrls,
    imageFallbacks: Array.isArray(product.imageFallbacks)
      ? product.imageFallbacks.map((u) => String(u || '').trim())
      : [],
    imagekitImages: Array.isArray(product.imagekitImages)
      ? product.imagekitImages.map((u) => String(u || '').trim())
      : [],
    thumbnail: product.thumbnail || imageUrls[0] || '',
    thumbnailFallback: product.thumbnailFallback || '',
    thumbnailImagekit: product.thumbnailImagekit || '',
    primaryImageIndex:
      product.primaryImageIndex != null && Number.isFinite(Number(product.primaryImageIndex))
        ? Number(product.primaryImageIndex)
        : 0,
    rating: product.rating || 0,
    reviews: product.reviews || 0,
    hasQR: false,
    deliveryAvailable: false,
    geek: product.geek || false,
    stock: product.inventory?.quantity ?? product.stock ?? 0,
    inStock: isProductInStock(product),
    isOutOfStock: product.isOutOfStock === true,
    isAvailable: isProductInStock(product) && product.isActive !== false,
    isActive: product.isActive !== false,
    isDeleted: product.isDeleted === true || product.deletedByAdmin === true,
    isFeatured: product.isFeatured || false,
    vendor: product.vendor?.name ?? product.storeName ?? product.store?.name ?? product.vendor ?? null,
    storeId: (product.storeId || product.store?._id || product.store?.id) ?? null,
    storeName: (product.storeName || product.store?.name) ?? null,
    vendorProductUrl: product.vendorProductUrl || product.vendor?.productUrl || null,
    vendorChatUrl: product.vendorChatUrl || product.vendor?.chatUrl || null,
    offerStartsAt: product.offerStartsAt || null,
    offerEndsAt: product.offerEndsAt || null,
    newBadgeDays: product.newBadgeDays != null ? Number(product.newBadgeDays) : 2,
    endingSoonBadgeDays: product.endingSoonBadgeDays != null ? Number(product.endingSoonBadgeDays) : 2,
    createdAt: product.createdAt || null,
    specifications: normalizeSpecifications(product),
    productSections:
      Array.isArray(product.productSections) && product.productSections.length > 0
        ? product.productSections
        : [
            product.inBestsellers && 'bestsellers',
            product.inWeeklyPromotion && 'weeklyPromotion',
            product.inPopular && 'popular'
          ].filter(Boolean)
  };
  const urlSlug =
    product.urlSlug != null && String(product.urlSlug).trim()
      ? String(product.urlSlug).trim()
      : getProductUrlSlugSegment({ name: product.name, id: idVal, _id: idVal });
  const priceMatch = product.priceMatch || null;
  const priceMatchOthers = Array.isArray(product.priceMatchOthers) ? product.priceMatchOthers : [];
  return { ...shaped, urlSlug, priceMatch, priceMatchOthers };
}
